import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    Input,
    InputSignal,
    OnDestroy,
    signal,
    Signal,
    WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslocoService } from '@ngneat/transloco';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { CustomCkEditorComponent } from 'app/core/custom-ck-editor/custom-ck-editor.component';
import { DataFieldDto, DataManagementExtra, DataTableDto } from 'app/data-api';
import { dataTypesFormly } from 'app/modules/data-module/data-management/components/data-fields/data-fields.data';
import { DataManagementService } from 'app/modules/data-module/data-management/data-management.service';
import { SysService } from 'app/modules/org-sys-area/services/sys.service';
import { BimService } from 'app/services/bim/bim.service';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { DataTableService } from '../../data-table/data-table.service';
import { getDmCostElementsFields } from './system-table-fields/dm-cost-elements.fields';
import { getDmCostObjectFields } from './system-table-fields/dm-cost-object.fields';
import { getDmFilesFields } from './system-table-fields/dm-files.fields';
import { getDmServicesFields } from './system-table-fields/dm-services.fields';
import { getDmTransactionsFields } from './system-table-fields/dm-transactions.fields';

@Component({
    selector: 'data-table-row-detail',
    imports: [
        CommonModule,
        FormlyModule,
        ReactiveFormsModule,
        MatTabsModule,
        CustomCkEditorComponent,
    ],
    templateUrl: './edit-data-table-row-detail.component.html',
    styleUrl: './edit-data-table-row-detail.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditDataTableRowDetailComponent implements OnDestroy {
    private _destroy: Subject<void> = new Subject<void>();
    private subscription: Subscription = new Subscription();

    @Input() selectedRowSignal!: Signal<any>;
    selectedModuleTitle: InputSignal<string> = input();

    form: FormGroup = new FormGroup({});
    model: any = {};

    fields: WritableSignal<FormlyFieldConfig[]> = signal([]);
    datamanagement: DataTableDto;
    _translocoContent: any;
    _systemTableTranslocoContent: WritableSignal<any> = signal(null);

    data: Signal<any> = computed(() => this._dataManagementService.data());
    index: number;
    constructor(
        private service: DataTableService,
        private _dataManagementService: DataManagementService,
        private bimService: BimService,
        private _translocoService: TranslocoService,
        private sysService: SysService
    ) {
        this._translocoContent = toSignal(this._translocoService.selectTranslation());

        this._translocoService
            .selectTranslation('data')
            .pipe(takeUntil(this._destroy))
            .subscribe(content => {
                this._systemTableTranslocoContent.set(content);
            });

        // effect(() => {
        //     this.setupForm();
        // });

        effect(() => {
            let selectedRow = structuredClone(this.selectedRowSignal());
            const data = this.data() as any[];
            const rowFromData = data.find(
                (d: any) => JSON.stringify(d) === JSON.stringify(selectedRow)
            );
            this.form.patchValue(rowFromData);
            this.index = data.indexOf(rowFromData);
        });
    }

    ngOnDestroy(): void {
        this._destroy.next();
        this._destroy.complete();
        this.subscription.unsubscribe();
    }

    formIsValid(): boolean {
        return this.form.valid;
    }

    setupForm(disabled = true): void {
        const selectedModule = this._dataManagementService.selectedDataTable();
        this.datamanagement = selectedModule;

        const selectedModuleTitel = this.selectedModuleTitle()
            ? this.selectedModuleTitle()
            : selectedModule?.tableName;

        if (
            this.selectedModuleTitle() ||
            selectedModule?.organisation === '00000000-0000-0000-0000-000000000000'
        ) {
            switch (selectedModuleTitel) {
                case 'dm_cost_elements':
                    this.fields.set(
                        getDmCostElementsFields(disabled, this._systemTableTranslocoContent)
                    );
                    break;
                case 'dm_cost_objects':
                    this.fields.set(
                        getDmCostObjectFields(disabled, this._systemTableTranslocoContent)
                    );
                    break;
                case 'dm_files':
                    this.fields.set(getDmFilesFields(disabled, this._systemTableTranslocoContent));
                    break;
                case 'dm_services':
                    this.fields.set(
                        getDmServicesFields(disabled, this._systemTableTranslocoContent)
                    );
                    break;
                case 'dm_transactions':
                    this.fields.set(
                        getDmTransactionsFields(
                            this.sysService.state(),
                            disabled,
                            this._systemTableTranslocoContent
                        )
                    );
                    break;
            }
        } else {
            this.setUpDynamicForm(disabled);
        }
    }

    private setUpDynamicForm(disabled: boolean): void {
        const keys: { type: string; key: string }[] = this.objectKeysAndTypes(this.datamanagement);

        //Create fields from object keys
        const fields: FormlyFieldConfig[] = [];

        keys.forEach((key: any) => {
            if (key.key === 'area' || key.key === 'system' || key.key === 'organisation') {
                fields.push({
                    className: 'flex-1',
                    type: dataTypesFormly[key.type],
                    key: key.key,
                    props: {
                        label: key.key,
                        required: true,
                        valueProp: 'title',
                        labelProp: 'title',
                        attributes: {
                            selectionType: key.displayName,
                        },
                        disabled: disabled,
                    },
                });
            } else {
                fields.push({
                    className: 'flex-1',
                    type: dataTypesFormly[key.type],
                    key: key.key,
                    props: {
                        label: key.displayName,
                        required: true,
                        disabled: disabled,
                    },
                });
            }
        });

        //Create group fields
        const fieldGroups = [];
        for (let i = 0; i < fields.length; i += 2) {
            fieldGroups.push({
                fieldGroupClassName: 'flex flex-row gap-2 flex-wrap',
                fieldGroup: fields.slice(i, i + 2), // Take 2 fields at a time
            });
        }

        //Put groups into fields signal
        this.fields.set(fieldGroups);
    }

    objectKeysAndTypes(dm: DataTableDto): { type: string; key: string }[] {
        let keys = [];

        dm?.fields.forEach((field: DataFieldDto) => {
            keys.push({
                type: field.dtype,
                key: field.title,
                displayName: field.fieldName,
            });
        });

        return keys;
    }

    save(): void {
        if (!this.form.valid) {
            throw new Error();
        }

        let updatedData;
        if (this.index === -1) {
            updatedData = [...this.data(), this.form.value];
        } else {
            updatedData = this.data();
            updatedData[this.index] = this.form.value;
        }
        const data: DataManagementExtra = {
            table: this._dataManagementService.selectedDataTable(),
            data: updatedData,
        };

        this.bimService
            .insertData(data)
            .pipe(takeUntil(this._destroy))
            .subscribe(() => {
                this.bimService
                    .getDbData(this._dataManagementService.selectedDataTable())
                    .pipe(takeUntil(this._destroy))
                    .subscribe(response => {
                        this._dataManagementService.data.set(response);
                    });
            });
    }
}
